import React, { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { Facebook, Inbox, MessageCircle, Settings, Zap } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { bumpUnreadDMs, bumpUnreadComments, clearUnreadDMs, clearUnreadComments } from '../../redux/facebookSlice';

// No "Content" tab — Facebook Page publishing already lives in the Content
// Calendar's cross-post feature (FacebookPagePublishingService), not here.
const tabs = [
  { name: 'Messages', to: '/dashboard/facebook/messages', icon: Inbox, unreadKey: 'unreadDMs' },
  { name: 'Comments', to: '/dashboard/facebook/comments', icon: MessageCircle, unreadKey: 'unreadComments' },
  { name: 'Automation Rule', to: '/dashboard/facebook/automation', icon: Zap },
  { name: 'Settings', to: '/dashboard/facebook/settings', icon: Settings },
];

export default function FacebookDashboardLayout() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { unreadDMs, unreadComments } = useSelector((s) => s.facebook);
  const unreadByKey = { unreadDMs, unreadComments };

  // Mirrors InstagramDashboardLayout.jsx's persistent-socket unread-badge
  // pattern — stays connected across all tabs so switching away doesn't mean
  // missing new activity until you switch back.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'] });

    socket.on('inbox:new_message', ({ message }) => {
      if (message?.channel === 'messenger' && message?.direction === 'inbound' && !pathnameRef.current.endsWith('/messages')) {
        dispatch(bumpUnreadDMs());
      }
    });
    socket.on('facebook:new_comment', () => {
      if (!pathnameRef.current.endsWith('/comments')) {
        dispatch(bumpUnreadComments());
      }
    });

    return () => socket.disconnect();
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname.endsWith('/messages')) dispatch(clearUnreadDMs());
    if (location.pathname.endsWith('/comments')) dispatch(clearUnreadComments());
  }, [location.pathname, dispatch]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 md:px-6 pt-4 md:pt-6 bg-white border-b border-[#D8DDE6]">
        <div className="mb-4">
          <PageHeader
            title="Facebook Workspace"
            subtitle="Manage Facebook Page Messenger and comments — connected to your CRM."
            icon={Facebook}
          />
        </div>
        <nav className="flex space-x-1 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.to;
            const unread = tab.unreadKey ? unreadByKey[tab.unreadKey] : 0;
            return (
              <NavLink
                key={tab.name}
                to={tab.to}
                className={`relative flex shrink-0 items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-[#1877F2] text-[#1877F2]'
                    : 'border-transparent text-[#5C5C5C] hover:text-[#2E2E2E] hover:border-[#E5E5E5]'
                }`}
              >
                <span className="relative">
                  <Icon size={16} />
                  {unread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[#1877F2] text-white text-[9px] font-bold flex items-center justify-center leading-none">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </span>
                {tab.name}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-[#F3F3F3] flex flex-col">
        <Outlet />
      </div>
    </div>
  );
}
